import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Menu,
  Check,
  Loader2,
  LogOut,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Avatar, Button, Row, Col, Dropdown } from "antd";
import useAuthStore from "stores/AuthStore/AuthStore";
import { renderErrorNotifications } from "helpers/error.helpers";
import { signOutPortal } from "commons/signOutPortal";
import {
  listAccessibleAccounts,
  switchAccount,
} from "pages/Auth/login.service";

const Header = ({ onMenuClick }) => {
  const { userData, isProxy } = useAuthStore();
  const setUserData = useAuthStore((s) => s.setUserData);
  const [accounts, setAccounts] = useState([]);
  const [switchingId, setSwitchingId] = useState(null);
  const [open, setOpen] = useState(false);

  const loadAccounts = useCallback(async () => {
    const { data, errors } = await listAccessibleAccounts();
    if (errors?.length > 0) return;
    setAccounts(data?.accounts ?? []);
  }, []);

  useEffect(() => {
    // Proxy (impersonation) sessions cannot switch accounts, so skip the fetch.
    if (isProxy) return;
    loadAccounts();
  }, [loadAccounts, isProxy]);

  const handleSwitch = async (account) => {
    if (account.account_id === userData?.account_id) return;
    setOpen(false);
    setSwitchingId(account.account_id);
    const { data, errors } = await switchAccount({
      account_id: account.account_id,
    });
    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      setSwitchingId(null);
      return;
    }
    if (!data?.token) {
      renderErrorNotifications([
        { message: data?.message || "This account is not active." },
      ]);
      setSwitchingId(null);
      return;
    }
    setUserData({ user: data.account_info, token: data.token });
    window.location.href = "/shoup-community/dashboard";
  };

  const switchingAccount = accounts.find(
    (account) => account.account_id === switchingId
  );

  const renderAccountRow = (account) => {
    const isCurrent = account.account_id === userData?.account_id;
    const disabled = Boolean(switchingId) || !account.is_active;
    return (
      <button
        key={account.account_id}
        type="button"
        disabled={disabled && !isCurrent}
        onClick={() => (isCurrent ? setOpen(false) : handleSwitch(account))}
        className={`group flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-all duration-150 ${
          isCurrent
            ? "bg-primary/10 ring-1 ring-inset ring-primary/30"
            : "hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        }`}
      >
        <Avatar
          size={38}
          shape="square"
          src={account.logo_url || undefined}
          className="!rounded-xl shrink-0"
          style={{ backgroundColor: "#7c3aed" }}
        >
          {account.name?.[0]?.toUpperCase()}
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-foreground">
            {account.name}
          </span>
          <span className="text-xs text-muted-foreground">
            {account.is_owner ? "Owner" : "Member"}
            {!account.is_active && " · Pending"}
          </span>
        </div>
        {switchingId === account.account_id ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
        ) : isCurrent ? (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </button>
    );
  };

  const dropdownPanel = (
    <div className="w-[300px] overflow-hidden rounded-2xl border border-border/60 bg-popover p-2 shadow-elevated animate-in fade-in zoom-in-95 duration-150">
      {!isProxy && accounts.length > 0 && (
        <>
          <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {accounts.length > 1 ? "Switch account" : "Account"}
          </p>
          <div className="flex flex-col gap-0.5">
            {accounts.map(renderAccountRow)}
          </div>
          <div className="my-2 h-px bg-border/60" />
        </>
      )}
      <button
        type="button"
        onClick={() => signOutPortal()}
        className="flex w-full items-center gap-2.5 rounded-xl p-2.5 text-left text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        Logout
      </button>
    </div>
  );

  const userBlock = (
    <button
      type="button"
      className={`flex items-center gap-2.5 rounded-full border py-1 pl-1 pr-2 transition-all duration-200 ${
        open
          ? "border-primary/40 bg-muted shadow-sm"
          : "border-border/50 bg-card/60 hover:border-primary/30 hover:bg-muted"
      }`}
    >
      <Avatar
        src={userData?.avatarUrl}
        size={34}
        style={{ backgroundColor: "#7c3aed" }}
      >
        {userData?.name?.[0]?.toUpperCase()}
      </Avatar>
      <div className="hidden flex-col items-start leading-tight sm:flex">
        <span className="text-sm font-semibold text-foreground">
          {userData?.name}
        </span>
        <span className="text-[11px] font-medium text-muted-foreground">
          {userData?.is_owner ? "Owner" : "Member"}
        </span>
      </div>
      <ChevronDown
        className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
  );

  const headerBar = (
    <header className="sticky top-0 z-30 flex h-16 w-full items-center justify-between border-b border-border/50 bg-background/80 px-4 md:px-6 backdrop-blur-xl">
      <Row align="middle">
        <Col>
          <Button
            type="text"
            size="small"
            onClick={onMenuClick}
            className="mr-2 lg:hidden"
          >
            <Menu className="h-5 w-5 text-foreground" />
          </Button>
        </Col>
      </Row>

      <Dropdown
        dropdownRender={() => dropdownPanel}
        trigger={["click"]}
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
      >
        {userBlock}
      </Dropdown>
    </header>
  );

  return (
    <>
      {headerBar}
      {switchingAccount &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-8 animate-in fade-in duration-300"
            style={{
              background:
                "radial-gradient(circle at 50% 38%, hsl(var(--primary) / 0.22), transparent 55%), hsl(var(--background) / 0.92)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
            }}
          >
            <div className="relative flex h-32 w-32 items-center justify-center animate-in zoom-in-95 duration-500">
              <span className="absolute h-28 w-28 rounded-full bg-primary/30 blur-2xl animate-pulse" />
              <span className="absolute h-[96px] w-[96px] rounded-full border border-primary/40 animate-ping" />
              <span className="absolute h-[112px] w-[112px] rounded-full border-2 border-primary/15 border-t-primary animate-spin" />
              <Avatar
                size={80}
                src={switchingAccount.logo_url || undefined}
                className="shadow-elevated ring-2 ring-background"
                style={{ backgroundColor: "#7c3aed", fontSize: 30 }}
              >
                {switchingAccount.name?.[0]?.toUpperCase()}
              </Avatar>
            </div>

            <div className="flex flex-col items-center gap-2 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                Switching account
              </p>
              <p className="text-2xl font-bold text-foreground">
                {switchingAccount.name}
              </p>
              <div className="mt-2 flex items-center gap-1.5">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className="h-1.5 w-1.5 rounded-full bg-primary animate-bounce"
                    style={{ animationDelay: `${dot * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Header;
